/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { cssVar } from '@touchplan/css-var';
import { AbstractDisplayLine } from '@appPlanApplication/timeline/AbstractDisplayLine';
import { applyDisplayLineTextStyling } from '@appPlanApplication/timeline/TimelineHelpers';
import { Container } from 'pixi.js-legacy';
import { takeUntil } from 'rxjs/operators';
/**
 * Visual configuration settings for the ghost line.
 * These settings control the appearance of both the line and its label.
 */
const settings = {
	lineColor: cssVar.ghostlineBlue, // Ghost Line specific overrides
	borderColor: cssVar.almostBlack, // The left and right border edges
	borderWidth: 1, // 1px left and right border
	lineWidth: 20, // Ghost Line specific overrides
	labelColor: cssVar.almostBlack, // Ghost Line label color override
};
/**
 * ActiveLineDrawer is responsible for rendering the active line indicator in the canvas.
 * The active line shows the current date with a vertical line and text label.
 */
export class GhostLineDrawer extends AbstractDisplayLine {
	/**
	 * The position the GhostLine is currently set to, in world space.
	 * When the Ghost Line is enabled, we provide the world X coordinate for it to draw at
	 * The Ghost Line will always draw at that location until a new value is provided.
	 * @private
	 */
	private _startX: number = 0;
	/**
	 * The label text for the ghost line. This value is intended to remain unchanged even as the parent line is modifed
	 * We provide this value when activating the ghost line.
	 * @private
	 */
	private _startText: string = ``;
	/**
	 * Creates a new ActiveLineDrawer instance.
	 * @param _canvasAngularLand - Interface to Angular services/dependencies
	 * @param application - The canvas application instance
	 * @param layer - The layer the ghost line should live in.
	 */
	constructor(
		private _canvasAngularLand: CanvasAngularLand,
		application: AbstractApplication,
		layer: LayerPaths
	) {
		super(application, layer);
		this.hide();

		this.parentApplication.camera.changed$.pipe(takeUntil(this._shutdownSubject$)).subscribe((_) => {
			this.update();
		});
	}

	/**
	 * We expose a getter for retrieving the container object holding the Ghost Line because it allows us to
	 * sort children in the layer/have more control over sorting order in the scene graph without requiring
	 * strict constructor initialization order.
	 */
	public get container(): Container {
		return this._container;
	}
	/**
	 * Updates the ghost line's position based on camera transforms.
	 * This ensures the line stays correctly positioned during pan/zoom operations.
	 * @param _ - Delta Time elapsed since last update
	 */
	public update(_?: number): void {
		if (!this._container) {
			return;
		}
		// Y-value of 0 to start at the top of the screen
		super.update(this.parentApplication.camera.realToScreenX(this._startX), 0);
	}
	/**
	 * Draws the ghost line visualization.
	 * Creates a vertical line and positions the rotated date label.
	 */
	public draw(): void {
		this._graphics.clear();

		this._label.text = this._startText;
		const height: number = this.parentApplication.camera.getScreen().height;

		// Draw the main line
		this._graphics.beginFill(settings.lineColor);
		this._graphics.drawRect(0, 0, settings.lineWidth, height);
		this._graphics.endFill();

		// Draw the border. We draw the border edges after the main line so it draws _above_ the line operation
		this._graphics.beginFill(settings.borderColor);
		this._graphics.drawRect(0, 0, settings.borderWidth, height); // left edge starts at 0
		this._graphics.drawRect(settings.lineWidth, 0, settings.borderWidth, height); // right edge starts at the line width
		this._graphics.endFill();

		applyDisplayLineTextStyling(this._label);
		// Add the header height to the label Y to offset it from the top of the screen
		this._label.y = this._label.y + cssVar.headerHeight_px;
		this._label.style.fill = settings.labelColor;
	}
	/**
	 * Cleans up resources when the drawer is destroyed.
	 */
	public destroy(opts?: IDestroyOptions): void {
		super.destroy(opts);
		this._canvasAngularLand = null;
	}

	/**
	 * This enables the ghost line and will draw it forever at a fixed position unless explicitly disabled.
	 * @param text - The text label that appears
	 * @param startX - The world space X coordinate to draw the line at. It will always fill the height of the screen
	 */
	public enable(text: string, startX: number): void {
		this._startText = text;
		this._startX = startX;
		this.show();
	}
	/**
	 * This disables the ghost line. It's essentially an alias for `hide()`
	 */
	public disable(): void {
		this.hide();
	}
}
